import { parsePrice } from '@cf-shared/shared-util';
import { TrialPeriod } from 'pages/subscribe/checkout-trial';
import { CloudFunctionsService } from 'services/cloud-functions.service';
import { SettingsService } from 'services/settings.service';
import { AuthService } from 'services/auth.service';
import { Injectable } from '@angular/core';
import { UrlQueryService } from './url-query-service';
import { LocationService } from './location.service';
import { ConfigDbService } from 'services/config-db.service';
import { ConfigKey } from 'services/config-keys';
import { PaddleCheckoutConfig, PAYPRO_TEST_MODE_KEY_SESSION_ITEM } from '@cf-shared/store-interface';
import { WebPaymentProviderMode } from '@cf-shared/data-model/select-web-provider';
import { getPaddleBucketId } from './paddle-bucket-id';
import { BOOT_LOCALE } from 'i18n/i18n.service';
import { redactSecretQueryParams } from '@cf-shared/error-describe';

export interface PlanOption {
  buyUrl: string;
  id: string;
  name: string; // this is the name of the plan in PayPro
  title: string; // this is the name shown to the user
  price: number;
  currency: string;
  priceText: string;
  preDiscount?: string;
  saving?: string;
  numMonths: number;
  /** The free trial this plan's checkout includes, read from the Paddle price (#6145).
   *  Undefined means no trial on the Paddle rail, and "not known" on every other rail;
   *  either way the Subscribe screen then promises no trial. */
  trialPeriod?: TrialPeriod;
}

/** The PayPro test-mode key an E2E run left in session storage, or undefined
 *  for everybody else. */
function readPayProTestModeKey(): string | undefined {
  return sessionStorage.getItem(PAYPRO_TEST_MODE_KEY_SESSION_ITEM) || undefined;
}

@Injectable()
export class PayProPriceFetcher {
  planOptions: PlanOption[] = [];

  // which web rail the server picked for this user. The subscribe page reads
  // this after fetchPricesAsync to choose the PPG-iframe vs Paddle-inline render
  // path. Defaults to PPG until the dispatcher answers. (#3861)
  provider: 'payPro' | 'paddle' = 'payPro';
  // the Paddle checkout config (env, client token, price ids, checkout id,
  // is_e2e), set only when `provider === 'paddle'`. The subscribe page inits
  // Paddle.js and opens the inline checkout from it.
  paddleConfig?: PaddleCheckoutConfig;

  /**
   * #4962: whether the server built this checkout with the referral prices, so the
   * friend really is getting their free month. Read from the server's answer and
   * NEVER recomputed here — only the server can see the referral record and resolve
   * the inviter's flag.
   */
  referralFreeMonthApplied = false;

  /**
   * #4962: whether to offer the "were you invited?" code box. Carried on BOTH
   * branches below, because a signed-in user with no referral is not a referred
   * friend — the routing override does not engage for them, so under `payProOnly`
   * or a low ramp they are returned the PayPro branch, which is where the box would
   * otherwise be missing for exactly the people it exists for.
   */
  offerReferralCodeEntry = false;

  constructor(
    private cloudFunctions: CloudFunctionsService,
    private settings: SettingsService,
    private urlQuery: UrlQueryService,
    private location: LocationService,
    private config: ConfigDbService,
    private auth: AuthService,
  ) {
  }

  /**
   * Throw away the cached answer so the next `fetchPricesAsync` really asks the
   * server again (#4962).
   *
   * Needed because claiming a referral code CHANGES the answer: the caller becomes a
   * referred friend, so the server will now hand back referral prices, the free-month
   * line, and — on the PayPro rail — a different provider entirely. Without this the
   * page would accept a valid code, record the claim, and go on showing 14 days.
   *
   * The trap it exists for is that this only bites on ONE rail. On the Paddle branch
   * `planOptions` stays empty (Paddle plans are built page-side), so the
   * load-once test below is false and a second call reaches the server anyway. On the
   * PayPro branch it is populated, the test is true, and the server is never asked
   * again — so the bug is invisible until somebody claims a code on the PayPro rail.
   */
  resetForRefetch(): void {
    this.planOptions = [];
    this.provider = 'payPro';
    this.paddleConfig = undefined;
    this.referralFreeMonthApplied = false;
    this.offerReferralCodeEntry = false;
  }

  async fetchPricesAsync(): Promise<PlanOption[]> {

    // only load once per session
    if (this.planOptions.length > 0) {
      return this.planOptions
    }
    console.log('Checkout: Fetching prices.');

    // make sure we have determined the country
    await this.location.locationReady;

    // the base64 UTM/tracking blob passed back to the IPN webhook (and on to
    // MixPanel for each recurring purchase). The shared helper also defaults
    // utm_clid to the device id so we can match the user's initial session
    // (often from Instagram) with the server-created account.
    let telemetryDataBase64 = this.urlQuery.getTrackingDataBase64();

    // read the per-user web-payment-provider mode from the config DB and pass it
    // to the server dispatcher, which applies selectWebProvider (decision 1).
    // Defaults to payProOnly when the flag is unset.
    //
    // Guard on a signed-in user: the config DB only loads after login (its
    // initializeAsync reads auth.user.uid), so getConfigValueAsync awaits a
    // `loaded` promise that NEVER resolves for a logged-out, purchase-first
    // buyer — which would hang fetchPrices until the 30s pricing timeout and
    // break the checkout for both PPG and Paddle. A logged-out buyer has no
    // per-user override anyway, so default them to payProOnly (the safe Phase-1
    // rail) and let the server re-evaluate. The forceWebProvider override below
    // is read from the URL, not the config DB, so it still works logged-out.
    const mode = this.auth.user
      ? ((await this.config.getConfigValueAsync<WebPaymentProviderMode>(ConfigKey.WebPaymentProvider)) ?? 'payProOnly')
      : 'payProOnly';

    // override: a `forceWebProvider` URL query param (payPro|paddle) forces the
    // rail for this one request so a test (E2E or manual) can exercise either
    // checkout without flipping the global WebPaymentProvider config. It only
    // picks the rail — grants are still gated by the webhook. Like the e2e-test
    // marker, it is read from the URL captured at app bootstrap, so it must be
    // present on a full page load of /subscribe.
    const forceProvider = this.urlQuery.getUrlParam('forceWebProvider') || undefined;

    // stable per-browser anon id for the Paddle rollout ramp. The server uses it
    // to bucket a LOGGED-OUT purchase-first buyer (a logged-in request buckets on
    // the user's email/uid instead). Minted once and reused so the buyer gets a
    // consistent provider across reloads (#3910). Always sent; harmless when the
    // server ignores it (logged-in, or PayPro modes).
    const bucketingId = getPaddleBucketId();

    // an E2E run proves it may have a PayPro test-mode checkout by sending the
    // test-mode key, which it stored in session storage before loading this page.
    // An ordinary browser has no such item and sends nothing.
    const payProTestModeKey = readPayProTestModeKey();

    // retry up to 3 times — pricing is critical for the subscribe page
    let productsQuery: Awaited<ReturnType<CloudFunctionsService['storeGetCheckout']>>;
    const maxAttempts = 3;
    for (let attempt = 1; attempt <= maxAttempts; attempt++) {
      try {
        productsQuery = await this.cloudFunctions.storeGetCheckout({
          domain: window.location.hostname,
          country: this.settings.locale.localeName,
          telemetryData: telemetryDataBase64,
          mode,
          forceProvider,
          bucketingId,
          // carried into the PayPro checkout URL as `x-locale`, so the welcome
          // email the IPN sends is written in the language the buyer was
          // actually looking at (#4244)
          uiLanguage: BOOT_LOCALE,
          payProTestModeKey,
        });
        if (!('error' in productsQuery)) break;
        console.warn(`Checkout: Attempt ${attempt}/${maxAttempts} returned error: ${productsQuery.error}`);
      } catch (err) {
        console.warn(`Checkout: Attempt ${attempt}/${maxAttempts} failed:`, err);
        if (attempt === maxAttempts) throw err;
      }
      if (attempt < maxAttempts) await new Promise(r => setTimeout(r, 1500));
    }

    if ('error' in productsQuery) {
      console.error(productsQuery.error);
      return this.planOptions; // empty — caller shows the pricing-failed alert
    }

    // Paddle branch: the dispatcher returned the inline-checkout config. The
    // subscribe page inits Paddle.js and builds its own plan options from a
    // PricePreview, so we return no PPG plans here.
    if (productsQuery.provider === 'paddle') {
      this.provider = 'paddle';
      this.paddleConfig = productsQuery;
      this.referralFreeMonthApplied = productsQuery.referralFreeMonthApplied === true;
      this.offerReferralCodeEntry = productsQuery.offerReferralCodeEntry === true;
      console.log(`Checkout: Paddle selected (env=${productsQuery.environment}, e2e=${productsQuery.isE2e}).`);
      return this.planOptions; // empty; Paddle plans are built page-side
    }

    // PayPro branch (provider 'payPro', or the legacy no-provider shape).
    this.provider = 'payPro';
    // the code box rides on this branch too — see the field's own comment.
    this.offerReferralCodeEntry = productsQuery.offerReferralCodeEntry === true;
    this.planOptions = productsQuery.products.map(p => ({
      title: p.title,
      name: p.name,
      id: p.id,
      buyUrl: p.buyUrl,
      price: p.displayPrice,
      currency: p.displayCurrency,
      priceText: p.displayPrice && p.displayCurrency ? `${p.displayPrice} ${p.displayCurrency}` : undefined,
      numMonths: p.numMonths,
    }));

    // this.planOptions = await Promise.all([this.getBuyOption('Monthly', 26958), this.getBuyOption('Yearly', 27543)]);
    // redacted: an E2E run's buy URLs carry the PayPro test-mode key, and every
    // console line here also becomes a Sentry breadcrumb
    console.log('PayPro: Prices received: ', redactSecretQueryParams(JSON.stringify(this.planOptions)));

    let priceM = parsePrice(this.planOptions[0].priceText);
    let priceY = parsePrice(this.planOptions[1].priceText);
    // generate the discount only if can parse prices correctly - just in case so we don't completely break for weird currencies
    if (priceM && priceY) {
      let preDiscount = priceM.value * 12;
      let saving = preDiscount - priceY.value;
      this.planOptions[1].preDiscount = `${priceM.currencyPre}${preDiscount.toFixed(2).replace('.', priceM.decimal)}${priceM.currencyPost}`;
      this.planOptions[1].saving = `${priceM.currencyPre}${saving.toFixed(2).replace('.', priceM.decimal)}${priceM.currencyPost}`;
    }

    console.log('PayPro: Prices parsed: ', redactSecretQueryParams(JSON.stringify(this.planOptions)));

    return this.planOptions;
  }
}
